/**
 * Unified design token system — premium gaming platform.
 * Single source of truth for all screens and components.
 *
 * Phase 6B: exact values extracted from Lovable design-master build
 * (styles-DT2NjvV8.css, routes-DWF25gRF.js, idpreview HTML).
 * No inferred or approximated values — all colors confirmed from source.
 */

export const DESIGN_TOKENS = {

  // ── Primitive palette ────────────────────────────────────────────────────
  // Source: --gold #c9a84c, --gold-soft #f0d78c, --gold-deep #8a7028
  //         --background #0b0b0b, --surface #131313, --surface-2 #1c1c1c
  //         --ink #f5f0e0, --ink-muted #a89f86
  primitive: {
    gold:       '#c9a84c',
    goldLight:  '#f0d78c',
    goldDark:   '#8a7028',
    goldGlow:   'rgba(201,168,76,0.25)',

    bg:       '#0b0b0b',
    surface1: '#131313',
    surface2: '#1c1c1c',
    surface3: '#242424',

    white:   '#f5f0e0',   // design master "white" is warm ivory, not #fff
    text:    '#f5f0e0',
    muted:   '#a89f86',

    teamBlue: '#3B82F6',
    teamRed:  '#E05252',
    green:    '#4ADE80',
    red:      '#EF4444',
  },

  // ── Semantic colors ──────────────────────────────────────────────────────
  color: {
    // Backgrounds — source: #0b0b0b / #131313 / #1c1c1c
    pageBg:         '#0b0b0b',
    cardBg:         '#131313',
    cardBgElevated: '#1c1c1c',
    inputBg:        '#242424',
    sidebarBg:      '#0d0d0d',

    // Borders — source: --hairline #c9a84c2e (gold at ~18% opacity)
    border:           'rgba(201,168,76,0.18)',   // --hairline exact
    borderStrong:     'rgba(201,168,76,0.35)',   // stronger hairline variant
    borderGold:       'rgba(201,168,76,0.25)',
    borderGoldStrong: 'rgba(201,168,76,0.50)',

    // Text — source: --ink #f5f0e0, --ink-muted #a89f86
    textPrimary:   '#f5f0e0',   // warm ivory/cream — NOT pure white
    textSecondary: '#a89f86',   // warm beige muted
    textMuted:     '#a89f86',
    textFaint:     'rgba(168,159,134,0.45)',
    white:         '#f5f0e0',   // canonical "white" in this palette is warm ivory

    // Brand — source: --gold #c9a84c, --gold-soft #f0d78c, --gold-deep #8a7028
    gold:       '#c9a84c',
    goldLight:  '#f0d78c',
    goldGlow:   'rgba(201,168,76,0.25)',
    goldOnGold: '#0b0b0b',   // source: btn-gold uses color:#0b0b0b (page bg on gold)

    // Team
    teamBlue: '#3B82F6',
    teamRed:  '#E05252',

    // State
    success:   '#4ADE80',
    error:     '#EF4444',
    warning:   '#F59E0B',
    successBg: 'rgba(74,222,128,0.08)',
    errorBg:   'rgba(239,68,68,0.08)',

    // Presence
    presenceOnline:  '#4ADE80',
    presenceInGame:  '#A855F7',
    presenceOffline: '#333333',
  },

  // ── Elevation / shadow ───────────────────────────────────────────────────
  // Source: --shadow-gold: 0 30px 80px -30px #c9a84c73
  //         --shadow-elegant: 0 25px 60px -20px #000c
  shadow: {
    card:     '0 1px 8px rgba(0,0,0,0.60)',
    modal:    '0 25px 60px -20px rgba(0,0,0,0.80)',
    glow:     '0 0 32px rgba(201,168,76,0.22)',
    glowMd:   '0 0 48px rgba(201,168,76,0.30)',
    hero:     '0 40px 120px -30px rgba(0,0,0,0.90)',
    gold:     '0 30px 80px -30px rgba(201,168,76,0.45)',   // --shadow-gold exact
    elegant:  '0 25px 60px -20px rgba(0,0,0,0.80)',       // --shadow-elegant exact
  },

  // ── Gradients ────────────────────────────────────────────────────────────
  // Source: --gradient-gold: linear-gradient(135deg, #f0d78c 0%, #c9a84c 50%, #8a7028 100%)
  gradient: {
    gold:     'linear-gradient(135deg, #f0d78c 0%, #c9a84c 50%, #8a7028 100%)',
    card:     'linear-gradient(135deg, #131313 0%, #0f0f0f 100%)',
    danger:   'linear-gradient(135deg, #dc2626, #b91c1c)',
    pageGlow: 'radial-gradient(ellipse at 60% 20%, rgba(201,168,76,0.10) 0%, transparent 55%)',
    heroGlow: 'radial-gradient(circle, rgba(201,168,76,0.25) 0%, transparent 60%)',
  },

  // ── Z-index scale ────────────────────────────────────────────────────────
  zIndex: {
    base:    0,
    card:    10,
    drawer:  40,
    nav:     50,
    modal:   60,
    toast:   70,
    overlay: 80,
  },

  // ── Spacing ──────────────────────────────────────────────────────────────
  space: {
    sm:   16,
    md:   24,
    lg:   40,
    xl:   64,
    xxl:  96,
  },

  // ── Border radius ────────────────────────────────────────────────────────
  radius: {
    xs:     4,
    sm:     8,
    md:     10,
    lg:     16,
    xl:     20,
    button: 9999,   // design master btn-gold uses border-radius: 9999px (pill)
    input:  8,
    pill:   9999,
    circle: '50%' as const,
  },

  // ── Typography ───────────────────────────────────────────────────────────
  // Source: --font-display: "Bebas Neue", "Barlow", sans-serif
  //         --font-sans: "Barlow", system-ui, sans-serif
  font: {
    /** Display / heading — Bebas Neue (design master exact) */
    heading: 'Bebas Neue, Barlow, sans-serif',
    /** UI / body — Barlow regular */
    sans:    'Barlow, sans-serif',
    /** Brand lockup (BrandName component only) */
    brand:   'Montserrat, sans-serif',
  },

  type: {
    xs:    { size: 11, lineHeight: 16,  weight: 400 },
    sm:    { size: 13, lineHeight: 18,  weight: 400 },
    base:  { size: 15, lineHeight: 24,  weight: 400 },
    md:    { size: 16, lineHeight: 24,  weight: 400 },
    lg:    { size: 18, lineHeight: 28,  weight: 600 },
    xl:    { size: 20, lineHeight: 28,  weight: 600 },
    '2xl': { size: 24, lineHeight: 32,  weight: 700 },
    '3xl': { size: 32, lineHeight: 40,  weight: 400 },  // Bebas Neue is weight 400
    '4xl': { size: 48, lineHeight: 52,  weight: 400 },  // Bebas Neue is weight 400

    // Semantic aliases
    heroHeading: {
      size: 80, lineHeight: 74, weight: 400,  // Bebas Neue — weight is always 400
    },
    sectionTitle: {
      size: 36, lineHeight: 38, weight: 400,
    },
    sectionLabel: {
      size: 11, lineHeight: 16, weight: 700,
      letterSpacing: '0.3em',
      textTransform: 'uppercase' as const,
    },
    cardHeading:  { size: 16, lineHeight: 20, weight: 400 },  // Bebas Neue
    pageHeading:  { size: 32, lineHeight: 34, weight: 400 },
    bodyText:     { size: 15, lineHeight: 24, weight: 400 },
    caption:      { size: 12, lineHeight: 16, weight: 400 },
    navItem:      { size: 13, lineHeight: 20, weight: 400, letterSpacing: '0.18em' },
    buttonLabel:  { size: 13, lineHeight: 20, weight: 600, letterSpacing: '0.20em' },
  },

  // ── Breakpoints ──────────────────────────────────────────────────────────
  breakpoint: {
    mobile:  0,
    tablet:  768,
    desktop: 1024,
    wide:    1440,
  },

  // ── Layout ───────────────────────────────────────────────────────────────
  layout: {
    topNavHeight:         64,
    // Native (Capacitor/iOS+Android): the fixed TopNav sits at the true
    // viewport top, so its box must grow by the status-bar/notch inset and
    // push its own content down by the same amount (box-sizing:border-box —
    // padding-top is absorbed into this height, inner 100%-height children
    // are unaffected). env() resolves to 0 in browsers/contexts with no
    // inset (desktop web), so this is a safe default everywhere.
    topNavHeightCss:      'calc(64px + env(safe-area-inset-top, 0px))',
    // Shared safe-area insets for any fixed/edge-anchored surface (Sidebar,
    // Drawer, Footer, toasts, …) — status bar / notch (top) and home
    // indicator / gesture bar (bottom).
    safeAreaTop:          'env(safe-area-inset-top, 0px)',
    safeAreaBottom:       'env(safe-area-inset-bottom, 0px)',
    contentMaxWidth:      1200,
    contentPadding:       'clamp(16px, 5vw, 80px)',
    cardGap:              16,
    sectionGap:           64,
    sectionGapMax:        96,
    pagePaddingMobile:    16,
    pagePaddingTablet:    24,
    pagePaddingDesktop:   40,
    headerHeightMobile:   60,
  },

  // ── Motion ───────────────────────────────────────────────────────────────
  motion: {
    // Design Master source: Tailwind --default-transition-duration: .15s
    fast:   150,
    // SBGDS extension — no Design Master source for these values
    normal: 250,
    slow:   400,
    // Design Master source: Tailwind --default-transition-timing-function
    easing:       'cubic-bezier(0.4, 0, 0.2, 1)',
    // SBGDS extension — spring easing not present in Design Master
    easingSpring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    // Design Master source: btn-gold / btn-gold-lg transition property list
    buttonTransition: 'transform .25s, box-shadow .25s, filter .25s',
    // Design Master source: Tailwind .transition class property list + 0.15s / ease-in-out
    defaultTransition: 'color .15s cubic-bezier(0.4,0,0.2,1), background-color .15s cubic-bezier(0.4,0,0.2,1), border-color .15s cubic-bezier(0.4,0,0.2,1), opacity .15s cubic-bezier(0.4,0,0.2,1)',
  },

  // ── Hover interaction tokens ─────────────────────────────────────────────
  hover: {
    // Design Master source: .btn-gold:hover { filter: brightness(1.08); transform: translateY(-1px) }
    buttonFilter:    'brightness(1.08)',
    buttonTransform: 'translateY(-1px)',

    // Design Master source: article.hover:border-gold/60
    // gold/60 = color-mix(in oklab, #c9a84c 60%, transparent) ≈ rgba(201,168,76,0.60)
    cardBorder: 'rgba(201,168,76,0.60)',

    // Design Master source: nav <a> hover:text-gold
    navTextColor: '#c9a84c',

    // ── SBGDS extensions — no Design Master source ───────────────────────
    // These exist in the app context (auth inputs, avatar) but the Design Master
    // landing page has no equivalent element to extract from.
    avatarBorder: 'rgba(201,168,76,0.35)',
    inputBorder:  'rgba(201,168,76,0.35)',
    inputShadow:  '0 0 0 3px rgba(201,168,76,0.12)',
  },

} as const;

export type DesignTokens = typeof DESIGN_TOKENS;

/** Shorthand alias used throughout new components. */
export const DT = DESIGN_TOKENS;

/** DS2 gold action gradient — Figma-confirmed 104.72deg (vs DT's 135deg). */
const DS2_GOLD_GRADIENT = 'linear-gradient(104.72deg, #F0D78C 0%, #C9A84C 50%, #8A7028 100%)';
/** DS2 dark surface — sidebar + footer band background (confirmed exact from Figma). */
const DS2_SURFACE = '#1F1F1F';

/**
 * DS2 — Design System V2 tokens.
 *
 * Additive only. Confirmed exact from Figma Dev Mode export (node 696:979)
 * for the new application-wide visual language (Sidebar, Footer, Landing).
 * Deliberately kept separate from DESIGN_TOKENS/DT — those remain in active
 * use across every other existing screen (Home, Auth, Game UI, etc.) and
 * must not change as a side effect of this redesign. Migrating other
 * screens onto DS2 is a distinct future decision.
 */
export const DS2 = {
  color: {
    surface:    DS2_SURFACE,  // sidebar + footer band background (confirmed exact)
    pageBg:     '#0B0B0B',  // matches DT.color.pageBg — listed for DS2 completeness
    textBright: '#F5F5F5',  // hero/body copy color in the approved design (brighter than DT.color.textSecondary)
    gold:       '#c9a84c',  // matches DT.color.gold
    goldOutlineText: '#D4AF37', // "Log In" ghost-button text color (confirmed, distinct hex from DT.color.gold)
    /** Neutral near-white section divider (Figma "Vector 4"/"Vector 5") — distinct from DT.color.border (gold-tinted). Landing-scoped. */
    divider: 'rgba(245,245,245,0.2)',
    /** Feature-card border — confirmed lighter opacity than DT.color.borderStrong. Scoped to NumberedFeatureCard. */
    cardBorder: 'rgba(201,168,76,0.18)',
  },
  font: {
    heading: "'Montserrat', sans-serif",
    body:    "'Roboto', sans-serif",
    small:   "'Inter', sans-serif",
  },
  radius: {
    /** Rounded-rectangle buttons — NOT a pill. Distinct from DT.radius.button (9999). */
    button: 16,
    card:   23,
  },
  gradient: {
    /** Same stops as DT.gradient.gold, confirmed angle from Figma export (104.72deg vs DT's 135deg). */
    gold: DS2_GOLD_GRADIENT,
  },
  glow: {
    /** Shared atmospheric radial-glow formula — confirmed exact, reused at 3 sizes on Landing. */
    formula: (widthPx: number, heightPx: number) =>
      ({
        width:  widthPx,
        height: heightPx,
        background: 'radial-gradient(59.27% 93.13% at 50% 50%, #C9A84C 0%, rgba(201,168,76,0) 60%)',
        opacity: 0.25,
        // Figma 1920-export exact: blur(24.35px)
        filter: 'blur(24.35px)',
        pointerEvents: 'none' as const,
      }),
  },
  layout: {
    /**
     * Landing/DS2 content max-width — confirmed from Figma's 1728px desktop
     * container. Fluid below this reference width (clamp/percentage), never
     * a hard breakpoint. Distinct from DT.layout.contentMaxWidth (1200),
     * which remains unchanged for every other screen.
     */
    contentMaxWidth: 1728,
  },
  /**
   * Fluid type scale — proportional scale correction (~0.75×) applied to the
   * original Figma-reference maxima, which were reaching their ceiling by
   * ~1200-1600px viewport and rendering pinned-to-max on every normal desktop
   * (see Oversized Landing Investigation). Each `pref` is a vw coefficient
   * recalculated so the value reaches its new `max` at ~2200px — never
   * earlier — restoring genuine fluid scaling across 1440-1920px instead of
   * a de-facto fixed size. `min` values (inline rem literals at each call
   * site) are unchanged from before this pass.
   */
  type: {
    eyebrow:        { max: 19, pref: 0.88,  weight: 400 },
    heroHeading:    { max: 53, pref: 2.4 },
    sectionHeading: { max: 55, pref: 2.51, lineHeight: 1.2 },
    cardNumber:     { max: 39, pref: 1.76 },
    cardTitle:      { max: 25, pref: 1.12 },
    cardBody:       { max: 15, pref: 0.68 },
    paragraph:      { max: 21, pref: 0.94, lineHeight: 1.70 },
    footerHeading:  { max: 17, pref: 0.79 },
    footerLink:     { max: 14, pref: 0.62 },
  },
  spacing: {
    /** Feature card internal padding — scaled from the 46px reference. */
    cardPadding: 27,
    /** Feature card grid gap — scaled from the 35px reference. */
    cardGap: 21,
    /** Hero CTA row gap — fluid, reaches new max at ~2200px (see `type` doc above). */
    ctaGap: { max: 29, pref: 1.33 },
    /** Hero image corner radius — fluid, reaches new max at ~2200px. */
    heroImageRadius: { max: 31, pref: 1.39 },
  },

  /**
   * Canonical modal/dialog system — SINGLE source of truth for every true
   * application modal (approved Figma "Frame 100" design, 2026-08-20).
   * Consumed exclusively by `DS2Modal` (components/ui/DS2Modal.tsx), which
   * generates the one shared stylesheet from these values. Modal consumers
   * must never copy these into their own CSS.
   *
   * Figma reference frame is 798×410 desktop; every dimension is a
   * design-space maximum — DS2Modal renders them through clamp() so the
   * modal stays proportional and readable at real sizes.
   */
  modal: {
    /** Above DT.zIndex.overlay (80) so the canonical modal clears RoundEndOverlay. */
    zIndex: 100,
    /** Backdrop: whole app blurred + subtly darkened; modal itself stays sharp. */
    overlayTint:         'rgba(0,0,0,0.35)',
    /** Stronger tint when backdrop-filter is unsupported (graceful fallback). */
    overlayTintFallback: 'rgba(0,0,0,0.60)',
    blurPx:              8,
    /** Restrained premium entrance — backdrop fade + subtle panel scale. */
    transitionMs:        180,
    maxWidth:     798,
    /** Same authoritative surface as the desktop Sidebar (DS2.color.surface) — not black. */
    headerBg:     DS2_SURFACE,
    titleColor:   '#F5F5F5',
    bodyBg:       '#D9D9D9',
    bodyText:     '#0B0B0B',
    /** Square top corners (body meets header), strongly rounded bottom. */
    bottomRadius: 45,
    buttonRadius: 14.85,
    actionGap:    29,
    /** Inline error treatment for a failed modal action. */
    errorColor:   '#961515',
    action: {
      gold: {
        background: DS2_GOLD_GRADIENT,
        border:     '1px solid #C9A84C',
        shadow:     '0 29.7px 79.2px -29.7px rgba(201,168,76,0.45)',
        text:       '#0B0B0B',
      },
      red: {
        background: '#961515',
        border:     '1px solid #961515',
        shadow:     'none',
        text:       '#D9D9D9',
      },
      /** Restrained premium dark green — no DS2 success token existed; built to
       *  sit in the gold/red family (same 104.72deg three-stop structure). */
      green: {
        background: 'linear-gradient(104.72deg, #3EA06A 0%, #1F7A46 50%, #10502D 100%)',
        border:     '1px solid #1F7A46',
        shadow:     '0 29.7px 79.2px -29.7px rgba(31,122,70,0.45)',
        text:       '#F5F5F5',
      },
      /** Quiet outline action for genuinely neutral choices on the gray body. */
      neutral: {
        background: 'transparent',
        border:     '1px solid rgba(11,11,11,0.40)',
        shadow:     'none',
        text:       '#0B0B0B',
      },
    },
  },
} as const;
